import { holds } from '../../lib/permissionMatch.ts';
import { payable } from '../../lib/proformaState.ts';
import { changeableBeforePayment, unlockAllowed } from './changeActions.ts';
import { type PaymentActionFacts, paymentActions } from './paymentActions.ts';

export interface InvoiceGateFacts extends PaymentActionFacts {
  offerId: number | null;
  invoiceRecipient?: {
    blocked: boolean;
    blockReason?: string;
    email?: string;
  } | null;
  documentVersion?: number;
  /** Sends accepted and not carried out yet (waiting for the stored PDF). */
  queuedSends?: readonly QueuedSend[];
}

export type QueuedSend = {
  version: number;
  recipientEmail: string;
  createdAt?: string;
};

/** A queued send this old has most likely not been picked up by background sending. */
export const QUEUED_SEND_STALE_MS = 5 * 60_000;

const minutesAgo = (createdAt: string | undefined, now: number) =>
  createdAt
    ? Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 60_000))
    : null;

export function queuedSendText(q: QueuedSend, now = Date.now()): string {
  const age = minutesAgo(q.createdAt, now);
  return `Queued${age == null ? '' : ` ${age} min ago`} - version ${q.version} to ${q.recipientEmail}, sent when its PDF is stored`;
}

/** Shown under a queued send that has waited too long: the way forward is Retry now. */
export function queuedSendWarning(
  q: QueuedSend,
  now = Date.now(),
): string | null {
  if (!q.createdAt) return null;
  return now - new Date(q.createdAt).getTime() > QUEUED_SEND_STALE_MS
    ? 'Still not sent. Background sending may be off: press Retry now to make the PDF and send it.'
    : null;
}

/** The queued send of the current version to the current recipient, which Send retries. */
export function queuedSendFor(invoice: InvoiceGateFacts): QueuedSend | null {
  const email = invoice.invoiceRecipient?.email?.trim().toLowerCase();
  return (
    (invoice.queuedSends ?? []).find(
      (q) =>
        q.version === (invoice.documentVersion ?? 1) &&
        email != null &&
        q.recipientEmail.trim().toLowerCase() === email,
    ) ?? null
  );
}

export type SendGate =
  | { shown: false }
  | {
      shown: true;
      blockedReason: string | null;
      /** A send of this version to this recipient is queued: Send retries it ("Retry now"). */
      retry: boolean;
    };

export interface InvoiceActionGates {
  settle: boolean;
  addReport: boolean;
  removeReport: boolean;
  recordPayment: boolean;
  amend: boolean;
  cancel: boolean;
  refreshBillTo: boolean;
  /** Start KYC for the payer: only while the proforma is Open (server: kycTargets). */
  kyc: boolean;
  mondayCreate: boolean;
  send: SendGate;
}

export function invoiceActionGates(
  invoice: InvoiceGateFacts,
  permissions: readonly string[],
): InvoiceActionGates {
  const moveMoney = holds(permissions, 'invoices:update-status');
  const write = holds(permissions, 'invoices:write');
  const live = invoice.status !== 'cancelled';
  const changeable = changeableBeforePayment(
    invoice.status,
    invoice.paidAmount,
  );
  const payment = paymentActions(invoice);
  const onOffer = invoice.offerId != null;
  const recipient = invoice.invoiceRecipient;
  return {
    settle: moveMoney && payment.settle,
    addReport: moveMoney && payment.addReport,
    removeReport: moveMoney,
    recordPayment: moveMoney && payable(invoice.status),
    amend: write && changeable,
    cancel: write && changeable,
    refreshBillTo: write && changeable,
    kyc: write && payable(invoice.status),
    // Only once the proforma is Settled - its real invoice issued; money on an Open one is not enough.
    mondayCreate: moveMoney && onOffer && invoice.status === 'paid',
    send:
      holds(permissions, 'invoices:send') && live && onOffer
        ? {
            shown: true,
            blockedReason: recipient?.blocked
              ? (recipient.blockReason ?? 'This invoice cannot be sent.')
              : null,
            retry: queuedSendFor(invoice) != null,
          }
        : { shown: false },
  };
}

export function unlockOffered(
  offer: { accepted: boolean; mayUnlock?: boolean },
  offerInvoices: { status: string; paidAmount?: number | string | null }[],
): boolean {
  return (
    offer.accepted && offer.mayUnlock === true && unlockAllowed(offerInvoices)
  );
}

export function installmentIssueAllowed(
  item: { id: number; seq?: number },
  parts: { status: string; splitPlanItemId?: number | null }[],
  permissions: readonly string[],
  offerOwner = false,
): boolean {
  if (item.seq != null && item.seq <= 1) return false;
  if (holds(permissions, 'offers:issue-installment-all')) return true;
  return (
    (holds(permissions, 'invoices:write') || offerOwner) &&
    parts.some((p) => p.splitPlanItemId === item.id && p.status === 'cancelled')
  );
}

export interface PayerOption {
  id: number;
  name: string;
  countryCode: string;
  clientType: string;
  selectable: boolean;
  note: string | null;
}
